/**
 * Sewing Floor Board — the headline strip above the board. See `design-notes.md`.
 *
 * Report-local cards rendering server-computed values (§4.6) — nothing promoted to a shared
 * component. Every figure comes from the `totals` view already divided: floor efficiency and floor
 * WFT are weighted ratios and must never be summed or averaged in the browser (§4.3).
 */

import { faTrophy } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { cn } from '@/lib/utils';

import type { BoardRow, Tier } from './columns';

/** One row of the `totals` view. */
export type BoardTotals = Record<string, unknown>;

function num(source: BoardTotals | undefined, field: string): number | null {
  if (source === undefined) return null;
  const value = source[field];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function pct(value: number | null, digits = 1): string {
  return value === null ? '—' : `${value.toFixed(digits)}%`;
}

/** Whole pieces / line counts — target so far can be fractional in SQL; display never is. */
function count(value: number | null): string {
  return value === null ? '—' : Math.round(value).toLocaleString();
}

interface Scale {
  strip: string;
  label: string;
  figure: string;
  against: string;
  note: string;
  chip: string;
  chipId: string;
  chipValue: string;
  chipIcon: string;
}

/** The same tiers the rows use. `md` and `sm` share a size: below `md` the rows are the constraint. */
const SCALE: Record<Tier, Scale> = {
  xl: {
    chipIcon: 'size-5',
    strip: 'gap-x-10 px-6 py-4',
    label: 'text-base tracking-widest',
    figure: 'text-6xl',
    against: 'text-xl',
    note: 'text-base',
    chip: 'gap-2.5 rounded-lg px-4 py-1.5',
    chipId: 'text-2xl',
    chipValue: 'text-xl',
  },
  lg: {
    chipIcon: 'size-4',
    strip: 'gap-x-8 px-6 py-3',
    label: 'text-sm tracking-widest',
    figure: 'text-5xl',
    against: 'text-lg',
    note: 'text-sm',
    chip: 'gap-2 rounded-lg px-3 py-1',
    chipId: 'text-xl',
    chipValue: 'text-lg',
  },
  md: {
    chipIcon: 'size-3.5',
    strip: 'gap-x-8 px-6 py-2',
    label: 'text-xs tracking-widest',
    figure: 'text-4xl',
    against: 'text-base',
    note: 'text-xs',
    chip: 'gap-2 rounded-md px-2.5 py-0.5',
    chipId: 'text-lg',
    chipValue: 'text-base',
  },
  sm: {
    chipIcon: 'size-3.5',
    strip: 'gap-x-8 px-6 py-2',
    label: 'text-xs tracking-widest',
    figure: 'text-4xl',
    against: 'text-base',
    note: 'text-xs',
    chip: 'gap-2 rounded-md px-2.5 py-0.5',
    chipId: 'text-lg',
    chipValue: 'text-base',
  },
  desk: {
    chipIcon: 'size-3',
    strip: 'gap-x-8 px-1 py-3',
    label: 'text-xs tracking-widest',
    figure: 'text-3xl',
    against: 'text-sm',
    note: 'text-xs',
    chip: 'gap-2 rounded-md px-2.5 py-1',
    chipId: 'text-sm',
    chipValue: 'text-sm',
  },
};

/** A headline figure and what it is measured against. `against` is required — §4.6: a figure with a target shows it. */
function Figure({
  label,
  value,
  against,
  tone,
  note,
  s,
}: {
  label: string;
  value: string;
  against: string;
  tone: string;
  note?: string;
  s: Scale;
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <span className={cn('font-semibold text-muted-foreground uppercase', s.label)}>{label}</span>
      <div className="flex flex-wrap items-baseline gap-2.5">
        <span className={cn('leading-none font-bold tabular-nums', s.figure, tone)}>{value}</span>
        <span className={cn('text-muted-foreground', s.against)}>{against}</span>
      </div>
      {note === undefined ? null : (
        <span className={cn('text-muted-foreground', s.note)}>{note}</span>
      )}
    </div>
  );
}

/** Gold, silver, bronze — first, second, third. Keyed by the rank the SERVER assigned. */
const PODIUM: Record<number, string> = {
  1: 'text-medal-gold',
  2: 'text-medal-silver',
  3: 'text-medal-bronze',
};

/** A hairline between fields, as the design has. Decorative, so it is hidden from the reader. */
function Divider() {
  return <div className="w-px shrink-0 self-stretch bg-border" aria-hidden />;
}

/** Colour only, over two server-computed cells of one complete row — the narrow §4.3 case; not a new threshold. */
function toneFor(value: number | null, target: number | null, higherIsBetter: boolean): string {
  if (value === null || target === null) return '';
  const good = higherIsBetter ? value >= target : value <= target;
  return good ? 'text-status-ok-ink' : 'text-status-warning';
}

/**
 * The Day output figure's denominator. Prefers the elapsed-share target; falls back to the
 * whole-day plan (today's pre-change display) rather than to a bare count, so a known target is
 * never dropped (§4.6) — only when neither is loaded does the figure go without one.
 */
function outputAgainst(output: number | null, targetSoFar: number | null, target: number | null): string {
  if (targetSoFar !== null) {
    const ofToday = target === null ? '' : ` · of ${count(target)} today`;
    return `${count(output)} / ${count(targetSoFar)} pcs so far${ofToday}`;
  }
  if (target !== null) {
    return `${count(output)} / ${count(target)} pcs`;
  }
  return `${count(output)} pcs`;
}

export function BoardHeadline({
  totals,
  leaders,
  isLoading,
  tier,
}: {
  totals: BoardTotals | undefined;
  leaders: BoardRow[];
  isLoading: boolean;
  /** The board's measured density, so a dense floor's strip gives back the height its rows need. */
  tier: Tier;
}) {
  const s = SCALE[tier];

  if (isLoading || totals === undefined) {
    return <div className={cn('animate-pulse rounded-lg bg-muted', tier === 'desk' ? 'h-20' : 'h-28')} />;
  }

  const efficiency = num(totals, 'avg_efficiency');
  const efficiencyTarget = num(totals, 'efficiency_target');
  const wft = num(totals, 'avg_wft');
  const wftLimit = num(totals, 'wft_limit');
  const output = num(totals, 'day_output_pcs');
  const outputTarget = num(totals, 'day_output_target_pcs');
  const outputTargetSoFar = num(totals, 'day_output_target_so_far');
  const outputPct = num(totals, 'day_output_pct');
  const onTarget = num(totals, 'lines_on_target');
  const withTarget = num(totals, 'lines_with_target');
  const running = num(totals, 'running_line_count');
  const lines = num(totals, 'line_count');
  const planned = withTarget !== null && withTarget > 0; // the server chose floor plan vs factory constant; this reads the flag

  return (
    // One row always: same rule as the factory board strip — columns share width; Top lines chips
    // wrap inside their column rather than pushing the strip onto a second line.
    <div className={cn('flex flex-nowrap items-stretch border-b border-border', s.strip)}>
      <Figure
        s={s}
        label="Floor efficiency"
        value={pct(efficiency)}
        // Names WHICH target: the floor's own planned roll-up, or the factory constant. Different numbers.
        against={
          efficiencyTarget === null
            ? ''
            : `/ ${efficiencyTarget.toFixed(0)}% ${planned ? 'planned' : 'factory'} target`
        }
        tone={toneFor(efficiency, efficiencyTarget, true)}
        note={
          planned
            ? `${count(onTarget)} of ${count(withTarget)} lines on their own target`
            : `${count(running)} of ${count(lines)} lines running`
        }
      />
      <Divider />
      <Figure
        s={s}
        label="Day output"
        value={pct(outputPct, 0)}
        against={outputAgainst(output, outputTargetSoFar, outputTarget)}
        tone=""
        note={outputTarget === null ? 'No plan loaded for this floor today' : undefined}
      />
      <Divider />
      <Figure
        s={s}
        label="Floor WFT"
        value={pct(wft, 2)}
        against={wftLimit === null ? '' : `/ ${wftLimit.toFixed(1)}% limit`}
        tone={toneFor(wft, wftLimit, false)}
        note={`${count(running)} of ${count(lines)} lines running`}
      />
      <Divider />

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className={cn('font-semibold text-muted-foreground uppercase', s.label)}>
          Top lines
        </span>
        {leaders.length === 0 ? (
          <span className={cn('text-muted-foreground', s.against)}>No line is running.</span>
        ) : (
          <div className="flex flex-wrap gap-2">
            {leaders.map((line) => {
              const value = line['efficiency'];
              const rank = typeof line['efficiency_rank'] === 'number' ? line['efficiency_rank'] : 0;
              return (
                <span
                  key={String(line['sfts_line'])}
                  className={cn(
                    'flex items-center border border-status-ok/40 bg-status-ok/10',
                    s.chip,
                  )}
                >
                  {/* Decorative, so the rank is repeated as text — colour alone is never the signal (§4.6, §4.9). */}
                  <FontAwesomeIcon
                    icon={faTrophy}
                    className={cn('shrink-0', PODIUM[rank] ?? 'text-muted-foreground', s.chipIcon)}
                    aria-hidden
                  />
                  <span className="sr-only">{`Rank ${String(rank)}: `}</span>
                  <span className={cn('font-bold', s.chipId)}>{String(line['line_label'])}</span>
                  <span
                    className={cn('font-semibold text-status-ok-ink tabular-nums', s.chipValue)}
                  >
                    {typeof value === 'number' ? `${value.toFixed(1)}%` : '—'}
                  </span>
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
